<template>
  <div>
    <div v-if="loading" class="loader-container">
      <site-loader />
    </div>
    <div
      v-if="images && !loading"
      class="listing-view"
      :style="{ 'padding-top': siteHeaderHeight }"
    >
      <site-header />
      <v-container class="py-0 py-sm-3" fluid>
        <listing-toolbar v-if="!isPortfolioSite && !isMobile" />
      </v-container>
      <v-sheet max-width="1400px" class="mx-auto px-0">
        <site-gallery
          v-if="hasPictures"
          class="mx-0 mb-7"
          is-listing-view
          :images="
            currentListing.pictures.length
              ? currentListing.pictures
              : [{ original: currentListing.picture }]
          "
          count
        />
        <listing-title
          v-if="hasPictures"
          :listing="currentListing"
          :reviews="reviews"
        />
        <v-row justify="space-between" no-gutters class="mt-8">
          <v-col cols="12" md="8" class="pa-0 pe-sm-10 px-3 bottom-margin">
            <read-more :text="content" :initial-height="220" />
            <div v-if="currentListing.amenities" class="py-8">
              <v-sheet max-width="1400px" class="mx-auto">
                <h2 class="text-h5 mb-7 font-weight-semibold">
                  {{ $t("amenities") }}
                </h2>
                <listing-amenities :items="currentListing.amenities" expanded />
              </v-sheet>
            </div>
            <h2 class="text-h5 mb-3 font-weight-semibold">
              {{ $t("see_on_map") }}
            </h2>
            <div class="location rounded-xl overflow-hidden">
              <site-map
                v-if="hasPictures"
                :filtered-listings="[currentListing]"
              />
            </div>
          </v-col>
          <v-col class="d-none d-md-block" cols="12" md="4">
            <listing-info-card v-if="settings.site_type && !isPortfolioSite" />
          </v-col>
        </v-row>
        <v-row v-if="currentListing.model_url">
          <v-col>
            <div class="ma-0 ma-sm-8 rounded-xl overflow-auto">
              <iframe
                width="100%"
                :height="isMobileOrIPad ? 400 : 800"
                :src="currentListing.model_url"
                frameborder="0"
                allowfullscreen
                allow="xr-spatial-tracking"
              ></iframe>
            </div>
          </v-col>
        </v-row>
      </v-sheet>
      <listing-details :details="currentListing.details" />
      <listing-rules :rules="currentListing.rules" />
      <v-sheet
        max-width="1400"
        class="mx-auto pa-4 pb-8 gap-2"
        v-if="listingReviews && listingReviews.length && !isPortfolioSite"
      >
        <h2 class="text-h5 font-weight-semibold mb-6">{{ $t("reviews") }}</h2>
        <v-row>
          <v-col
            cols="12"
            sm="6"
            v-for="r in visibleReviews"
            :key="r.id || r.created_at"
          >
            <listing-review :review="r" />
          </v-col>
        </v-row>
        <span
          v-if="listingReviews.length > reviewsToShow"
          class="text-body-2 font-weight-regular text-decoration-underline cursor-pointer"
          @click="loadMoreReviews"
          >{{ $t("load_more_reviews") }}</span
        >
      </v-sheet>
      <!-- <site-footer /> -->
      <listing-bottom-navigation
        v-if="isMobileOrIPad && !isPortfolioSite"
        ref="bottomNav"
        :price="defaultPricing?.day_rate"
        :info="currentListing.reservation_info"
        :listing="currentListing"
      >
        <listing-info-card @close-dialog="closeBottomNav" />
      </listing-bottom-navigation>
    </div>
    <site-footer />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { LOCATION_IMAGE } from "@/consts/CMS";
import DeviceMixin from "@/mixins/device-mixin";
import MediaMixin from "@/mixins/media-mixin";
import StylesMixin from "@/mixins/styles-mixin";
import SiteHeader from "@/components/header/site-header.vue";
import ListingToolbar from "@/components/listing/listing-toolbar";
import ListingTitle from "@/components/listing/listing-title.vue";
import ListingAmenities from "@/components/listing/listing-amenities.vue";
import ListingInfoCard from "@/components/listing/listing-info-card.vue";
import ReadMore from "@/components/common/site-read-more";
import SiteGallery from "@/components/common/site-gallery.vue";
import ListingDetails from "@/components/listing/listing-details";
import ListingRules from "@/components/listing/listing-rules";
import SiteFooter from "@/components/footer/site-footer";
import ListingBottomNavigation from "@/components/listing/listing-bottom-navigation.vue";
import SiteLoader from "@/components/common/site-loader.vue";
import SiteMap from "@/views/site-map";
import ListingReview from "@/components/listing/listing-review.vue";
import { axios } from "@/store/fetch";

export default {
  name: "listing-view",
  components: {
    SiteFooter,
    SiteHeader,
    ListingToolbar,
    ListingTitle,
    ListingAmenities,
    ListingInfoCard,
    ReadMore,
    SiteGallery,
    ListingDetails,
    ListingRules,
    ListingBottomNavigation,
    SiteLoader,
    SiteMap,
    ListingReview,
  },
  mixins: [DeviceMixin, StylesMixin, MediaMixin],
  data: () => ({ LOCATION_IMAGE, listingReviews: null, reviewsToShow: 4 }),
  computed: {
    ...mapState({
      currentListing: (state) => state.listingsModule.currentListing,
      reviews: (state) => state.reviewsModule.reviews,
      loading: (state) => state.listingsModule.loading,
      settings: (state) => state.settingsModule.settings,
      currentPricing: (state) => state.listingsModule.currentPricing,
    }),
    defaultPricing() {
      return this.currentPricing?.rate_plans_pricings["default"];
    },
    isPortfolioSite() {
      return (
        this.settings.site_type === "agent_reserve" &&
        !this.$route.query.agent_id
      );
    },
    images() {
      const { picture, pictures } = this.currentListing || {};
      return pictures?.length
        ? pictures?.map(
            (i) =>
              i.thumbnail ||
              i.original.replace("upload/", "upload/c_fit,h_600/")
          )
        : [picture];
    },
    hasPictures() {
      return this.currentListing.pictures || this.currentListing.picture;
    },
    content() {
      const sections = [
        "summary",
        "space",
        "access",
        "neighborhood",
        "interactionWithGuests",
        "notes",
        "transit",
        "houseRules",
      ];
      return sections
        .map((s) => ({
          title: this.translateSectionTitle(s),
          content: this.currentListing?.marketing_content?.[s],
        }))
        .filter((s) => s.content);
    },
    reviews() {
      return this.reviewSummary || {};
    },
    visibleReviews() {
      return this.listingReviews
        ? this.listingReviews.slice(0, this.reviewsToShow)
        : [];
    },
  },
  methods: {
    ...mapActions("listingsModule", ["getListing", "pricingInfo"]),
    ...mapMutations("listingsModule", ["setCurrentPricing"]),
    closeBottomNav() {
      if (this.$refs.bottomNav) {
        this.$refs.bottomNav.dialog = false;
      }
    },
    async getListingReviews() {
      axios
        .get("/reviews", { params: { listing_id: this.$route.params.id } })
        .then((res) => {
          this.listingReviews = res.data.reviews;
        });
    },
    loadMoreReviews() {
      this.reviewsToShow += 4;
    },
    translateSectionTitle(sectionKey) {
      // Convert camelCase to underscore format for translation keys
      const keyMapping = {
        summary: "summary",
        space: "space",
        access: "access",
        neighborhood: "neighborhood",
        interactionWithGuests: "interaction_with_guests",
        notes: "notes",
        transit: "transit",
        houseRules: "house_rules_content",
      };
      return this.$t(keyMapping[sectionKey] || sectionKey);
    },
  },
  async mounted() {
    window.scrollTo({ top: 0 });
    await this.getListing(this.$route.params.id);
    this.setCurrentPricing();
    this.getListingReviews();
  },
};
</script>
<style scoped>
.location {
  height: 500px;
}
.bottom-margin {
  margin-bottom: 100px;
}
.loader-container {
  min-height: 100vh;
}
</style>
